import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { JSX } from "react";

/** The brand slogan, written out glyph-by-glyph once the intro settles. */
const SLOGAN = "Everything Over Packets";
/** Per-glyph cadence (ms) for the write-on stagger — what makes it read as
 *  "written" rather than a single block fade. */
const SLOGAN_STEP_MS = 55;

/**
 * Logo + slogan block for the sign-in screen. Focus-ins over a pulsing halo,
 * lifts when `ready`, then writes the slogan out glyph-by-glyph once
 * `taglineIn` flips (unless `reduced`). The logo + halo keep tracking the
 * cursor via `par`. Extracted from `LoginPage.tsx`.
 */
export function LoginHero({
  ready,
  taglineIn,
  reduced,
  par,
}: {
  ready: boolean;
  taglineIn: boolean;
  reduced: boolean;
  par: { x: number; y: number };
}): JSX.Element {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        transition: "transform 800ms cubic-bezier(0.16,1,0.3,1)",
        transform: ready ? "translateY(0)" : "translateY(12vh)",
      }}
    >
      <Box className="focus-in" sx={{ position: "relative" }}>
        <Box
          aria-hidden
          className="logo-halo"
          sx={{
            pointerEvents: "none",
            position: "absolute",
            left: "50%",
            top: "50%",
            zIndex: -10,
            height: "150%",
            width: "150%",
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            filter: "blur(16px)",
          }}
          style={{
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.32)}, transparent 70%)`,
          }}
        />
        <Box
          component="img"
          src="/xop-logo-dark.svg"
          alt="XOP Networks"
          draggable={false}
          sx={{ position: "relative", height: { xs: 160, sm: 192 }, width: "auto" }}
          style={{
            transform: `translate3d(${par.x * 7}px, ${par.y * 7}px, 0)`,
            transition: "transform 360ms ease-out",
          }}
        />
      </Box>
      {/* Slogan — writes itself out glyph-by-glyph once the lift settles.
          The full phrase is exposed once to assistive tech via `sr-only`
          (a kept globals.css a11y utility — and the test pins it); the
          visible per-glyph spans are decorative (`aria-hidden`). */}
      <Typography
        component="p"
        sx={{
          mt: -0.25,
          fontSize: { xs: 12, sm: 14 },
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.34em",
          color: (t) => alpha(t.palette.common.white, 0.7),
        }}
      >
        <span className="sr-only">{SLOGAN}</span>
        <span aria-hidden="true">
          {Array.from(SLOGAN).map((glyph, i) => {
            const writing = taglineIn && !reduced;
            return (
              <span
                key={i}
                className={writing ? "slogan-glyph slogan-glyph--write" : "slogan-glyph"}
                {...(writing
                  ? { style: { animationDelay: `${i * SLOGAN_STEP_MS}ms` } }
                  : {})}
              >
                {glyph === " " ? " " : glyph}
              </span>
            );
          })}
        </span>
      </Typography>
    </Box>
  );
}
